import moment from "moment";

/**
 * Returns true when `startDate` is at most `latestBooking` days away from now
 * (i.e., booking is too close to the start date for automated checkout).
 *
 * Returns false (= no constraint) when:
 *   - `latestBooking` is missing, NaN, zero, or negative
 *   - `startDate` is missing or invalid
 *
 * Matches the existing inline behavior in HolidayPackageDetails.tsx.
 */
export const isWithinLatestBooking = (
  startDate: string | Date | null | undefined,
  latestBooking: string | number | null | undefined
): boolean => {
  if (startDate == null) return false;
  if (latestBooking == null || latestBooking === "") return false;
  const days = Number(latestBooking);
  if (Number.isNaN(days) || days <= 0) return false;
  const daysFromNow = moment(startDate).diff(moment(), "days");
  return daysFromNow <= days;
};

/**
 * Days from now until `startDate`. Negative if startDate is in the past.
 * Returns Infinity if startDate is missing or invalid.
 */
export const daysUntilStart = (
  startDate: string | Date | null | undefined
): number => {
  if (startDate == null) return Infinity;
  const m = moment(startDate);
  if (!m.isValid()) return Infinity;
  return m.diff(moment(), "days");
};

interface BuildPackageRequestPayloadArgs {
  packageName: string;
  packageUuid: string;
  startDate: string | Date;
  endDate: string | Date;
  guests: number;
  price: number | string;
  hostName?: string;
  clientEmail: string;
}

/** Formats a date to DD/MM/YYYY, or returns "-" when it cannot be parsed. */
const formatRequestDate = (value: string | Date): string => {
  const m = moment(value);
  return m.isValid() ? m.format("DD/MM/YYYY") : "-";
};

/**
 * Builds the payload sent to POST /email/support for "send request" flows.
 * The detail page and the cart both call this so the email body is identical.
 *
 * `startDate`/`endDate` must be raw, moment-parseable dates (ISO or Date) —
 * do NOT pre-format them, otherwise they cannot be parsed here.
 */
export const buildPackageRequestPayload = (
  args: BuildPackageRequestPayloadArgs
): { name: string; email: string; message: string } => {
  const {
    packageName,
    packageUuid,
    startDate,
    endDate,
    guests,
    price,
    hostName,
    clientEmail,
  } = args;

  const lines = [
    "Richiesta di prenotazione per il pacchetto vacanza:",
    `- Pacchetto: ${packageName}`,
    `- Codice: ${packageUuid}`,
    `- Date: dal ${formatRequestDate(startDate)} al ${formatRequestDate(endDate)}`,
    `- Ospiti: ${guests}`,
    `- Prezzo stimato: ${price}`,
  ];
  if (hostName) {
    lines.push(`- Host: ${hostName}`);
  }

  return {
    name: `Richiesta pacchetto: ${packageName}`,
    email: clientEmail,
    message: lines.join("\n"),
  };
};
